import { z } from 'zod';

import { VENUE_SLOT_WINDOW_MAX_DAYS } from '../venue-slots-list-query/venue-slots-list-query';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The windows one venue is offering a member, inside a range (P25e).
 *
 * ## Deliberately NOT `makeListQuery`, for the admin sibling's reason
 *
 * The caller is a **picker**, and it draws every window in the range it is
 * showing. A `pageSize` would produce a chooser that silently stops partway
 * through the month — the control-that-lies defect — rather than a list that
 * pages. The bound that keeps the read honest is the window's own width, which
 * is why both ends are required and why the ceiling is shared with the admin
 * calendar rather than re-declared: two answers to *"how wide may a request
 * be?"* is one answer too many.
 *
 * ## `status` is absent, and that is the narrowing
 *
 * This is not the admin query with a different guard. A member is offered
 * exactly the windows they can actually book — `open`, still in the future by
 * `meetings.bookingLeadTimeMinutes`, and not already full — and every one of
 * those conditions is the **server's**, resolved from its own clock and its own
 * settings (N3, U9). A `status` parameter here could only ask for a window the
 * request would then refuse, which is the offering-what-cannot-be-had defect U0
 * forbids.
 *
 * Sorting is not offered: slots are ordered by time, the only order a picker has.
 */
export const MemberVenueSlotsListQuerySchema = z
  .object({
    /** Inclusive lower bound, an ISO instant. */
    from: z.iso.datetime(),
    /** Exclusive upper bound, an ISO instant. */
    to: z.iso.datetime(),
  })
  .strict()
  .refine((window) => Date.parse(window.to) > Date.parse(window.from), {
    message: 'The window must end after it starts.',
    path: ['to'],
  })
  .refine(
    (window) =>
      Date.parse(window.to) - Date.parse(window.from) <= VENUE_SLOT_WINDOW_MAX_DAYS * MS_PER_DAY,
    {
      message: `A window may span at most ${VENUE_SLOT_WINDOW_MAX_DAYS} days.`,
      path: ['to'],
    },
  );

export type MemberVenueSlotsListQuery = z.infer<typeof MemberVenueSlotsListQuerySchema>;
